"use client"
import React, {useId} from "react";
import styled, { css } from "styled-components";
import Select, {components} from "react-select";
import DropIco from "/public/assets/icons/drop-ico.png";

// ▼ 옵션 렌더링 커스터마이징
const CustomOption = (props) => {
    const {data} = props; // data = option 객체
    return (
        <components.Option
            {...props}
            className={`${props.className} ${data.className || ""}`}
        >
            <p>{data.label}</p>
            {data.icon && <span style={{marginRight: 6}}>{data.icon}</span>}
        </components.Option>
    );
};

// ▼ 드롭다운 화살표 커스터마이징
const DropdownIndicator = (props) => {
    return (
        components.DropdownIndicator && (
            <components.DropdownIndicator {...props}>
                <img src={DropIco?.src} alt="arrow" style={{width: 8}}/>
            </components.DropdownIndicator>
        )
    );
};

const CustomSingleValue = (props) => {
    const {data} = props; // data = 선택된 option 객체
    const Icon = data.icon;
    return (
        <components.SingleValue {...props}>
            <p>{data.label}</p>
            {Icon &&
                <span>
                    {typeof Icon === "function" ? <Icon size={18}/> : Icon}
                </span>
            }
        </components.SingleValue>
    );
};

const SelectBox = ({
                       id,
                       option,
                       size = "m",
                       classNamePrefix,
                       className,
                       placeholder,
                       onChange,
                       defaultValue,
                       value,
                       border,
                       isSearchable = false,
                       brand = false,
                       type = "default", // 버튼 스타일 default/solid
                       round = false,
                       capsule = false,
                       disabled = false,
                       full = false,
                       menuPlacement = "auto",
                       arrow = true,
                       hover = true,
                   }) => {
    const uid = useId();
    const stableId = id ?? `select-${uid}`;

    return (
        <SelectWrapper
            options={option}
            size={size}
            className={"react-select-container"}
            classNamePrefix={"react-select"}
            $full={full}
            placeholder={placeholder}
            onChange={onChange}
            defaultValue={defaultValue}
            value={value}
            border={border}
            isSearchable={isSearchable}
            $brand={brand}
            $type={type}
            $round={round}
            $capsule={capsule}
            $hover={hover}
            isDisabled={disabled}
            menuPlacement={menuPlacement}
            instanceId={stableId}
            inputId={`${stableId}-input`}
            aria-describedby={`${stableId}-placeholder`}
            /* ▼ 여기부터 포탈 관련 옵션만 추가 */
            menuPortalTarget={typeof window !== "undefined" ? document.body : null}
            menuPosition="fixed"
            menuShouldScrollIntoView={false}
            styles={{
                menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                }),
            }}
            /* ▲ 여기까지 */
            components={{
                ...(arrow ? {DropdownIndicator} : {DropdownIndicator: null}),
                IndicatorSeparator: () => null,
                Option: CustomOption,
                SingleValue: CustomSingleValue,
            }}
        />
    );
};

// ▼ 테마 색상/스타일 추출
export const pickPalette = (theme, $brand, $type) => {
    const brandKey = $brand ? "brand" : "black";
    const styleKey = $type === "solid" ? "solid" : "default";
    return {
        bg: theme.COLORS.BUTTON[brandKey][styleKey].bg,
        line: theme.COLORS.BUTTON[brandKey][styleKey].line ?? {},
        font: theme.COLORS.BUTTON.font[brandKey][styleKey],
    };
};

// ▼ 스타일
const SelectWrapper = styled(Select).attrs({
    classNamePrefix: "react-select",
    className: "react-select-container",
})`
    .react-select__value-container,
    .react-select__value-container--has-value {
        padding: 0 !important;
    }

    .react-select__value-container {
        display: flex;
        align-items: center;
        width: 100%;
        flex-wrap: nowrap;
    }

    &.react-select-container {
        width: ${({$full}) => ($full ? "100%" : "auto")};
        flex-shrink: 0;
    }

    .react-select__control {
        width: ${({$full}) => ($full ? "100%" : "auto")};
        min-width: 100px;
        padding: ${({theme, size}) => theme.SIZE.BUTTON.width.padding[size]};
        height: ${({theme, size}) => theme.SIZE.BUTTON.height[size]};
        font-size: ${({theme, size}) => theme.SIZE.BUTTON.font[size]};
        font-weight: ${({theme, size}) => theme.SIZE.BUTTON.weight[size]};
        border-radius: ${({theme, $round, $capsule, size}) =>
                $round
                        ? theme.SIZE.BUTTON.round[size]
                        : $capsule
                                ? theme.SIZE.BUTTON.capsule[size]
                                : "0"};

        color: ${({theme, $brand, $type}) =>
                pickPalette(theme, $brand, $type).font.primary};
        background-color: ${({theme, $brand, $type}) =>
                pickPalette(theme, $brand, $type).bg.primary};
        border: ${({theme, $brand, $type}) => {
            const line = pickPalette(theme, $brand, $type).line;
            return line?.primary ? `1px solid ${line.primary}` : "none";
        }};
        cursor: pointer;
        min-height: auto;
        box-shadow: none;
        transition: all 0.2s ease-in-out;
    }


    ${({ $hover, theme, $brand, $type }) => $hover && css`
        .react-select__control:hover {
            border-color: #D1D1D1
        }
    `}

    .react-select__control--is-disabled {
        pointer-events: none;
        color: ${({theme, $brand, $type}) =>
                pickPalette(theme, $brand, $type).font.disabled};
        background-color: ${({theme, $brand, $type}) =>
                pickPalette(theme, $brand, $type).bg.disabled};
        border-color: ${({theme, $brand, $type}) => {
            const line = pickPalette(theme, $brand, $type).line;
            return line?.disabled || "transparent";
        }};
        opacity: 1;
    }
    .react-select__control--menu-is-open{
        background-color: #fff;
        border-color: #564F67 !important;
        .react-select__value-container{
            color: #333;
        }
        .react-select__indicator{
            transform: rotate(180deg);
        }
    }

    .react-select__single-value {
        color: ${({theme, $brand, $type}) =>
                pickPalette(theme, $brand, $type).font.primary};
        font-size: ${({theme, size}) => theme.SIZE.BUTTON.font[size]};
        font-weight: 500;
        margin: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    .react-select__menu {
        background-color: #fff;
        font-size: ${({theme, size}) => theme.SIZE.BUTTON.font[size]};
        font-weight: ${({theme, size}) => theme.SIZE.BUTTON.weight[size]};
        box-shadow: 0 2px 8px -4px rgba(0, 0, 0, 0.2);
        filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.08));
        border-radius: 10px;
        overflow: hidden;
        z-index: 999;
        width: 100%;
    }

    .react-select__menu-list {
        max-height: 150px;
        width: auto;
        overflow-y: auto;
    }

    .react-select__indicator {
        padding: 6px;
    }

    .react-select__indicator-separator {
        display: none;
    }

    .react-select__option {
        background-color: transparent;
        color: black;
        text-align: left;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    .react-select__option:hover {
        background-color: ${({theme, $brand, $type}) =>
                pickPalette(theme, $brand, $type).bg.hover};
        color: ${({theme}) => theme.COLORS.BUTTON.black.default.bg.primary};
    }

    .react-select__option--is-selected {
        background-color: ${({theme}) =>
                theme.COLORS.BUTTON.black.default.bg.primary};
        color: #fff;
    }
`;

export default SelectBox;
